import type { ChampClass, ClassRosterEntry } from "../types";

export const DAYS_OF_WEEK = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export const CLASS_GENDERS = ["mixed", "sisters", "brothers"] as const;

// The stages that put a child on a class roster, in display order.
export const ROSTER_STAGES = ["registered", "trial", "waitlist"] as const;
export type RosterStage = (typeof ROSTER_STAGES)[number];

/** Prices are stored in cents; people type dollars. */
export const centsToDollars = (cents: number | null | undefined) =>
  cents == null ? "" : (cents / 100).toFixed(2);

export const dollarsToCents = (dollars: string | number | null | undefined) => {
  if (dollars == null || dollars === "") return null;
  const value = typeof dollars === "number" ? dollars : Number(dollars);
  if (Number.isNaN(value)) return null;
  return Math.round(value * 100);
};

export const formatPrice = (cents: number | null | undefined) =>
  cents == null ? "" : `$${centsToDollars(cents)}`;

/** "09:00:00" -> "9:00 AM". Postgres time columns come back with seconds. */
export const formatTime = (time: string | null | undefined) => {
  if (!time) return "";
  const [hoursText, minutes = "00"] = time.split(":");
  const hours = Number(hoursText);
  if (Number.isNaN(hours)) return time;
  const suffix = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 === 0 ? 12 : hours % 12;
  return `${displayHours}:${minutes} ${suffix}`;
};

export const formatTimeRange = (
  start: string | null | undefined,
  end: string | null | undefined,
) => [formatTime(start), formatTime(end)].filter(Boolean).join(" – ");

export const formatAgeRange = (
  min: number | null | undefined,
  max: number | null | undefined,
) => {
  if (min != null && max != null) return `${min}–${max}`;
  if (min != null) return `${min}+`;
  if (max != null) return `≤ ${max}`;
  return "";
};

export const formatHeadcount = (
  record: Pick<ChampClass, "nb_enrolled" | "capacity">,
) =>
  record.capacity != null
    ? `${record.nb_enrolled ?? 0} / ${record.capacity}`
    : `${record.nb_enrolled ?? 0}`;

/** Flatten roster rows into the columns Zakariya keeps in Excel today. */
export const toRosterExportRows = (
  rows: ClassRosterEntry[],
  className: string,
) =>
  rows.map((row) => ({
    class: className,
    status: row.stage,
    player_first_name: row.player_first_name ?? "",
    player_last_name: row.player_last_name ?? "",
    age: row.player_age ?? "",
    gender: row.player_gender ?? "",
    date_of_birth: row.date_of_birth ?? "",
    guardian_first_name: row.guardian_first_name ?? "",
    guardian_last_name: row.guardian_last_name ?? "",
    guardian_phone: row.guardian_phone ?? "",
    guardian_email: row.guardian_email ?? "",
    paid: row.paid ? "yes" : "no",
    // Deal amounts are whole dollars in Atomic CRM (unlike class prices).
    amount: row.amount ?? "",
    source: row.source ?? "",
    medical_notes: row.medical_notes ?? "",
    coach_notes: row.coach_notes ?? "",
  }));
